import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import SortableTableHeader from '../../components/SortableTableHeader';
import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

const BoxLottery = () => {
  const { token } = useSelector((state) => state.auth);
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, pageSize: 30, order: '' });
  const [tempFilters, setTempFilters] = useState({ page: 1, pageSize: 30, order: '' });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedLotteries, setSortedLotteries] = useState([]);
  const [loadingAction, setLoadingAction] = useState(false);
  const [showAddTicketsModal, setShowAddTicketsModal] = useState(false);
  const [showBulkTicketsModal, setShowBulkTicketsModal] = useState(false);
  const [showAddTicketsConfirmModal, setShowAddTicketsConfirmModal] = useState(false);
  const [showBulkTicketsConfirmModal, setShowBulkTicketsConfirmModal] = useState(false);
  const [selectedLottery, setSelectedLottery] = useState(null);
  const [tickets, setTickets] = useState('');
  const [bulkData, setBulkData] = useState({ users: '', tickets: '' });
  const { can } = usePermissions();

  const fetchLotteries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get('/api/admin/lottery/box-lottery', {
        headers: { token },
        params
      });

      if (response.data.success) {
        const newData = response.data.data.data;
        setLotteries(newData);
        setSortedLotteries(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0
        });
      }
    } catch (error) {
      console.error('Error fetching lotteries:', error);
      toast.error('Failed to fetch lotteries');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchLotteries();
  }, [fetchLotteries]);

  // Client-side sorting effect
  useEffect(() => {
    if (!lotteries.length) return;
    const sorted = [...lotteries].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key] ?? '';
      let bValue = b[sort.key] ?? '';
      if (sort.key === 'totalTickets' || sort.key === 'totalParticipants' || sort.key === 'totalAmount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === 'isActive' || sort.key === 'resultAnnounced') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === 'createdAt' || sort.key === 'startTime' || sort.key === 'endTime' || sort.key === 'resultTime') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedLotteries(sorted);
  }, [lotteries, sort]);

  const handleFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters });
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (newPage) => {
    handleFilterChange('page', newPage);
  };

  const handleAddTickets = async () => {
    setShowAddTicketsConfirmModal(true);
  };

  const confirmAddTickets = async () => {
    try {
      setLoadingAction(true);
      const ticketsArray = tickets.split(',').map(t => parseInt(t.trim())).filter(t => !isNaN(t));
      const response = await axiosInstance.post(
        `/api/admin/lottery/box-lottery/${selectedLottery.lotteryId}/add-tickets`,
        { tickets: ticketsArray },
        { headers: { token } }
      );
      if (response.data.success) {
        toast.success('Tickets added successfully');
        setShowAddTicketsModal(false);
        setShowAddTicketsConfirmModal(false);
        setSelectedLottery(null);
        setTickets('');
        fetchLotteries();
      }
    } catch (error) {
      console.error('Error adding tickets:', error);
      toast.error('Failed to add tickets');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleAddBulkTickets = async () => {
    setShowBulkTicketsConfirmModal(true);
  };

  const confirmAddBulkTickets = async () => {
    try {
      setLoadingAction(true);
      const response = await axiosInstance.post(
        `/api/admin/lottery/box-lottery/${selectedLottery.lotteryId}/add-bulk-tickets`,
        { users: parseInt(bulkData.users), tickets: parseInt(bulkData.tickets) },
        { headers: { token } }
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Bulk tickets added successfully');
        setShowBulkTicketsModal(false);
        setShowBulkTicketsConfirmModal(false);
        setSelectedLottery(null);
        setBulkData({ users: '', tickets: '' });
        fetchLotteries();
      }
    } catch (error) {
      console.error('Error adding bulk tickets:', error);
      toast.error('Failed to add bulk tickets');
    } finally {
      setLoadingAction(false);
    }
  };

  const openAddTicketsModal = (lottery) => {
    setSelectedLottery(lottery);
    setShowAddTicketsModal(true);
  };

  const openBulkTicketsModal = (lottery) => {
    setSelectedLottery(lottery);
    setShowBulkTicketsModal(true);
  };

  const clearFilters = () => {
    setFilters({ page: 1, pageSize: 30, order: '' });
    setTempFilters({ page: 1, pageSize: 30, order: '' });
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const getStatusBadge = (resultAnnounced) =>
    resultAnnounced ? (
      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
        Announced
      </span>
    ) : (
      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">
        Pending
      </span>
    );

  const thCls = "text-left";
  const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

  // Action Dropdown Component
  const ActionDropdown = ({ lottery }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Lottery actions"
          type="button"
          className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44">
        {can('GET:/api/admin/lottery/box-lottery/:lottery_id/tickets') && (
          <DropdownMenuItem asChild className="text-foreground">
            <Link to={`/lottery/box/${lottery.lotteryId}/tickets`}>View Tickets</Link>
          </DropdownMenuItem>
        )}
        {!lottery.resultAnnounced && (
          <>
            {can('POST:/api/admin/lottery/box-lottery/:lottery_id/add-tickets') && (
              <DropdownMenuItem className="text-foreground" onSelect={() => openAddTicketsModal(lottery)}>
                Add Tickets
              </DropdownMenuItem>
            )}
            {can('POST:/api/admin/lottery/box-lottery/:lottery_id/add-bulk-tickets') && (
              <DropdownMenuItem className="text-foreground" onSelect={() => openBulkTicketsModal(lottery)}>
                Add Bulk Tickets
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Services
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Box <span className="text-gradient-ginox">Lottery</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        View and manage box lottery data.
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Order
            </label>
            <select
              value={tempFilters.order}
              onChange={(e) => handleFilterChange("order", e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) => handleFilterChange("pageSize", parseInt(e.target.value))}
              className={inputCls}
            >
              {[10, 15, 25, 50, 100].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page
            </label>
            <select
              value={tempFilters.page}
              onChange={(e) => handleFilterChange("page", parseInt(e.target.value))}
              className={inputCls}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full border-separate border-spacing-0">
            <tbody>
              <tr>
                <td className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className={thCls}>S.No</SortableTableHeader>
                  <SortableTableHeader sortKey="lotteryId" currentSort={sort} onSort={handleSort} className={thCls}>Lottery ID</SortableTableHeader>
                  <SortableTableHeader sortKey="startTime" currentSort={sort} onSort={handleSort} className={thCls}>Start Time</SortableTableHeader>
                  <SortableTableHeader sortKey="endTime" currentSort={sort} onSort={handleSort} className={thCls}>End Time</SortableTableHeader>
                  <SortableTableHeader sortKey="resultTime" currentSort={sort} onSort={handleSort} className={thCls}>Result Time</SortableTableHeader>
                  <SortableTableHeader sortKey="ticketCount" currentSort={sort} onSort={handleSort} className={thCls}>Ticket Count</SortableTableHeader>
                  <SortableTableHeader sortKey="resultAnnounced" currentSort={sort} onSort={handleSort} className={thCls}>Status</SortableTableHeader>
                  <SortableTableHeader sortKey="winner" currentSort={sort} onSort={handleSort} className={thCls}>Winner</SortableTableHeader>
                  <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort} className={thCls}>Created At</SortableTableHeader>
                  <th className="border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedLotteries.map((lottery, index) => (
                  <tr key={lottery._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className={`${tdCls} font-mono`}>
                      {(filters.page - 1) * filters.pageSize + index + 1}
                    </td>
                    <td className={`${tdCls} font-mono`}>{lottery.lotteryId}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(lottery.startTime)}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(lottery.endTime)}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(lottery.resultTime)}</td>
                    <td className={`${tdCls} font-mono`}>{lottery.ticketCount}</td>
                    <td className={tdCls}>{getStatusBadge(lottery.resultAnnounced)}</td>
                    <td className={tdCls}>{lottery.winner || "-"}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(lottery.createdAt)}</td>
                    <td className={tdCls}>
                      <ActionDropdown lottery={lottery} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Add Tickets Modal */}
      {showAddTicketsModal && selectedLottery && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl w-full max-w-md">
            <h3 className="text-base font-semibold text-foreground mb-4">
              Add Tickets to Lottery {selectedLottery.lotteryId}
            </h3>
            <div className="mb-4">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Tickets (comma-separated numbers)
              </label>
              <input
                type="text"
                value={tickets}
                onChange={(e) => setTickets(e.target.value)}
                placeholder="1, 2, 3, 4, 5"
                className={inputCls}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowAddTicketsModal(false); setSelectedLottery(null); setTickets(""); }}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAddTickets(selectedLottery.lotteryId)}
                disabled={loadingAction || !tickets.trim()}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
              >
                {loadingAction ? "Adding..." : "Add Tickets"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Bulk Tickets Modal */}
      {showBulkTicketsModal && selectedLottery && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl w-full max-w-md">
            <h3 className="text-base font-semibold text-foreground mb-4">
              Add Bulk Tickets to Lottery {selectedLottery.lotteryId}
            </h3>
            <div className="mb-4">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Number of Users
              </label>
              <input
                type="number"
                value={bulkData.users}
                onChange={(e) => setBulkData((prev) => ({ ...prev, users: e.target.value }))}
                placeholder="5"
                className={inputCls}
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Total Tickets
              </label>
              <input
                type="number"
                value={bulkData.tickets}
                onChange={(e) => setBulkData((prev) => ({ ...prev, tickets: e.target.value }))}
                placeholder="50"
                className={inputCls}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowBulkTicketsModal(false); setSelectedLottery(null); setBulkData({ users: "", tickets: "" }); }}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAddBulkTickets(selectedLottery.lotteryId)}
                disabled={loadingAction || !bulkData.users || !bulkData.tickets}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
              >
                {loadingAction ? "Adding..." : "Add Bulk Tickets"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showAddTicketsConfirmModal}
        onClose={() => setShowAddTicketsConfirmModal(false)}
        onConfirm={confirmAddTickets}
        title="Add Tickets"
        message={`Are you sure you want to add tickets to lottery ${selectedLottery?.lotteryId}?`}
        confirmText="Add Tickets"
        loading={loadingAction}
        loadingText="Adding..."
      />
      <ConfirmationModal
        isOpen={showBulkTicketsConfirmModal}
        onClose={() => setShowBulkTicketsConfirmModal(false)}
        onConfirm={confirmAddBulkTickets}
        title="Add Bulk Tickets"
        message={`Are you sure you want to add ${bulkData.tickets} tickets for ${bulkData.users} users to lottery ${selectedLottery?.lotteryId}?`}
        confirmText="Add Bulk Tickets"
        loading={loadingAction}
        loadingText="Adding..."
      />
    </div>
  );
};

export default BoxLottery;
